import { c as _c } from "react/compiler-runtime";
import { OrderedOverlayProvider } from "@/components/ui/ordered-overlay";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import * as React from "react";
import cn from "twsx";

// Linear's sheet. A right sheet floats 8px inside the window with a 10px
// radius; a left sheet (mobile navigation, 330 wide) runs flush to the edge
// with its outer corners rounded at 12px. Both carry the border-secondary ring
// over the modal shadow, slide in from their edge on --motion-sheet-ease and
// slide back out the same way while the scrim fades. Sizes: `md` 576 and `lg`
// 720 for detail panels, `form` for the creation forms, `nav` for mobile
// navigation, and `default` (three quarters, up to 1024). `tone="sidebar"` is
// the navy workspace sidebar shown as a sheet on mobile.

/** Use when a side panel shows details or a form while the page stays in view. */
const Sheet = SheetPrimitive.Root;
const SheetTrigger = SheetPrimitive.Trigger;
const SheetClose = SheetPrimitive.Close;
const SheetPortal = SheetPrimitive.Portal;
const SheetOverlay = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("fixed inset-0 z-modal bg-scrim", "data-[state=open]:animate-scrim-in data-[state=closed]:animate-scrim-out motion-reduce:animate-none", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <SheetPrimitive.Overlay className={t1} {...props} ref={ref} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;
const sheetVariants = cva(["fixed z-modal flex flex-col overflow-y-auto shadow-sheet outline-none motion-reduce:animate-none", "[&>form]:flex [&>form]:min-h-0 [&>form]:flex-1 [&>form]:flex-col"], {
  variants: {
    side: {
      top: "inset-x-0 top-0 rounded-b-popover",
      bottom: "inset-x-0 bottom-0 rounded-t-popover",
      left: ["inset-y-0 left-0 h-dvh rounded-r-popover", "data-[state=open]:animate-sheet-in-left data-[state=closed]:animate-sheet-out-left"],
      right: ["inset-y-2 right-2 rounded-settings-list-view-item", "data-[state=open]:animate-sheet-in-right data-[state=closed]:animate-sheet-out-right"]
    },
    size: {
      default: "w-full sm:w-3/4 sm:max-w-5xl",
      md: "w-full sm:w-sheet-md",
      lg: "w-full sm:w-sheet-lg",
      form: "w-sheet-form max-w-full",
      nav: "w-sheet-nav"
    },
    tone: {
      surface: "bg-bg-surface text-text-primary",
      sidebar: "bg-sidebar-bg text-sidebar-text"
    }
  },
  compoundVariants: [{
    side: "right",
    size: ["default", "md", "lg", "nav"],
    class: "max-w-viewport-inset-sm"
  }],
  defaultVariants: {
    side: "right",
    size: "default",
    tone: "surface"
  }
});
const closeTone = {
  surface: "text-text-tertiary hover:bg-fill-hover-subtle hover:text-text-primary",
  sidebar: "text-sidebar-icon hover:bg-sidebar-hover hover:text-sidebar-text-strong"
} as const;

// `showClose={false}` is for sheets whose own header already holds controls in
// the top corner, such as the mobile navigation sheet; Escape and the overlay
// still close them.
type SheetContentProps = React.ComponentProps<typeof SheetPrimitive.Content> & VariantProps<typeof sheetVariants> & {
  showClose?: boolean;
};
const SheetContent = t0 => {
  const $ = _c(26);
  let children;
  let className;
  let props;
  let ref;
  let size;
  let t1;
  let t2;
  let tone;
  if ($[0] !== t0) {
    ({
      side: t1,
      size,
      tone,
      showClose: t2,
      className,
      children,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = ref;
    $[5] = size;
    $[6] = t1;
    $[7] = t2;
    $[8] = tone;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    ref = $[4];
    size = $[5];
    t1 = $[6];
    t2 = $[7];
    tone = $[8];
  }
  const side = t1 === undefined ? "right" : t1;
  const showClose = t2 === undefined ? true : t2;
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SheetOverlay />;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== className || $[11] !== side || $[12] !== size || $[13] !== tone) {
    t4 = cn(sheetVariants({
      side,
      size,
      tone
    }), className);
    $[10] = className;
    $[11] = side;
    $[12] = size;
    $[13] = tone;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== showClose || $[16] !== tone) {
    t5 = showClose && <SheetPrimitive.Close aria-label="Close" className={cn("absolute top-3 right-3 z-20", "flex size-7 items-center justify-center rounded-full outline-none", "focus-visible:ring-1 focus-visible:ring-accent disabled:pointer-events-none", closeTone[tone ?? "surface"])}><X className="size-4" /></SheetPrimitive.Close>;
    $[15] = showClose;
    $[16] = tone;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== children || $[19] !== t5) {
    t6 = <OrderedOverlayProvider>{t5}{children}</OrderedOverlayProvider>;
    $[18] = children;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== props || $[22] !== ref || $[23] !== t4 || $[24] !== t6) {
    t7 = <SheetPortal>{t3}<SheetPrimitive.Content ref={ref} className={t4} {...props}>{t6}</SheetPrimitive.Content></SheetPortal>;
    $[21] = props;
    $[22] = ref;
    $[23] = t4;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
};
SheetContent.displayName = SheetPrimitive.Content.displayName;
type SheetHeaderProps = Omit<React.HTMLAttributes<HTMLDivElement>, "className">;
const SheetHeader = t0 => {
  const $ = _c(7);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("flex shrink-0 flex-col gap-1 border-b border-border-subtle text-left", "pt-4 pr-12 pb-3 pl-5");
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== children || $[5] !== props) {
    t2 = <div {...props} className={t1}>{children}</div>;
    $[4] = children;
    $[5] = props;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
SheetHeader.displayName = "SheetHeader";

// The scrolling region between header and footer. `padding="none"` is for
// lists and tables that run edge to edge.
const SheetBody = t0 => {
  const $ = _c(9);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      padding: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const padding = t1 === undefined ? "default" : t1;
  const t2 = padding === "default" && "px-5 py-4";
  const t3 = padding === "form" && "px-8 py-10 md:px-14";
  let t4;
  if ($[3] !== t2 || $[4] !== t3) {
    t4 = cn("flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto", t2, t3);
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== props || $[7] !== t4) {
    t5 = <div {...props} className={t4} />;
    $[6] = props;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};
SheetBody.displayName = "SheetBody";
const SheetFooter = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex h-sheet-footer shrink-0 items-center justify-end gap-2", "border-t border-border-subtle bg-bg-surface px-4 pb-safe-bottom", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
SheetFooter.displayName = "SheetFooter";
type SheetTitleProps = Omit<React.ComponentProps<typeof SheetPrimitive.Title>, "className">;
const SheetTitle = t0 => {
  const $ = _c(6);
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = ref;
  } else {
    props = $[1];
    ref = $[2];
  }
  let t1;
  if ($[3] !== props || $[4] !== ref) {
    t1 = <SheetPrimitive.Title ref={ref} {...props} className="min-w-0 text-lg font-medium leading-6 text-text-primary" />;
    $[3] = props;
    $[4] = ref;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
SheetTitle.displayName = SheetPrimitive.Title.displayName;
const SheetDescription = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("text-base leading-5 text-text-tertiary", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <SheetPrimitive.Description ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
SheetDescription.displayName = SheetPrimitive.Description.displayName;
export { Sheet, SheetBody, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetOverlay, SheetPortal, SheetTitle, SheetTrigger };